import { isPlaced, rankFor, type RankId } from '@kolony/shared';
import type { RatingApi, GuestRankRecord } from '../api/rating';
import type { VersionedStore } from '../storage/storage';
import type { RankState } from '../storage/storage';
import type { RankView } from '../ui/rankInfo';

// The one place the client decides what rank a player has (H14/D201).
//
// Two sources, never mixed: a signed-in account is told by the server (`GET /me/rating`); a
// guest carries a server-issued record in local storage. Both funnel into one `RankView`, so
// the rail, the badge and the promotion never have to ask which kind of player this is.
//
// Nothing here computes a rating. The client is a courier: it stores what it was handed and
// asks the server to move it. The only thing this module owns is DISPLAY memory — which
// shields have been seen (so they stop being blurred) and which rank was last announced (so
// a promotion fires exactly once).

export interface RankServiceDeps {
  api: RatingApi;
  store: VersionedStore<RankState>;
  guestId: () => string;
  signedIn: () => boolean;
  /** Fired when a rank is newly attained and has not yet been announced. */
  onPromotion?: (rank: RankId, awarded: boolean) => void;
}

const UNRANKED: RankView = {
  rank: null,
  placed: false,
  ratedGames: 0,
  placementGames: 3,
  rating: null,
  everHeld: [],
  guest: true,
};

export class RankService {
  private view: RankView = UNRANKED;
  private readonly listeners = new Set<(v: RankView) => void>();

  constructor(private readonly deps: RankServiceDeps) {}

  current(): RankView {
    return this.view;
  }

  onChange(fn: (v: RankView) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Read the player's rank from whichever source owns it, and publish it. */
  async refresh(): Promise<RankView> {
    const state = this.deps.store.load();
    const everHeld = state.everHeld.filter(isRankId);
    if (this.deps.signedIn()) {
      try {
        const mine = await this.deps.api.myRating();
        this.publish({
          rank: mine.placed ? (mine.tier ?? null) : null,
          placed: mine.placed,
          ratedGames: mine.ratedGames,
          placementGames: mine.placementGames,
          rating: mine.rating ?? null,
          everHeld,
          guest: false,
        });
      } catch {
        // Offline or a dead session: keep whatever we last showed rather than flashing the
        // player back to Unranked, which reads as having lost something.
      }
      return this.view;
    }
    const rec = state.record;
    const ratedGames = rec?.ratedGames ?? 0;
    const placed = isPlaced(ratedGames);
    this.publish({
      rank: placed && rec ? rankFor(rec.rating) : null,
      placed,
      ratedGames,
      placementGames: UNRANKED.placementGames,
      rating: placed && rec ? rec.rating : null,
      everHeld,
      guest: true,
    });
    return this.view;
  }

  /**
   * Bank whatever is outstanding. For a guest that means asking the server to settle every
   * unbanked game; for an account the server already did it at finalize, so it is a re-read.
   * Safe to call on every launch and after every game — with nothing pending it is a no-op.
   *
   * Failure is silent by design: a guest whose settle call is refused (a bot at the table, a
   * game that did not count, no network) keeps the record they have and plays on.
   */
  async settle(): Promise<RankView> {
    if (this.deps.signedIn()) return this.refresh();
    const state = this.deps.store.load();
    try {
      const res = await this.deps.api.settle(this.deps.guestId(), state.record);
      if (res.settled && res.record) this.deps.store.save({ ...state, record: res.record });
    } catch {
      // keep the record we hold
    }
    return this.refresh();
  }

  /**
   * At sign-up: hand the account the rank built as a guest, and only then forget the local
   * copy. A failed claim keeps it for the next launch — losing a rank to a flaky network is
   * unforgivable, and the retry costs nothing.
   */
  async claimForAccount(): Promise<boolean> {
    const state = this.deps.store.load();
    if (!state.record) return false;
    try {
      const res = await this.deps.api.claim(state.record);
      if (!res.claimed) return false;
      this.deps.store.save({ ...state, record: null });
      await this.refresh();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Announce a rank the player has not been shown yet. Returns what to display, or null when
   * there is nothing new — the caller can call this freely after any refresh.
   *
   * A DEMOTION is recorded silently: `lastShown` moves down without firing, so dropping a
   * band never gets a full-screen moment, and climbing back gets one again.
   */
  takePromotion(): { rank: RankId; awarded: boolean } | null {
    const state = this.deps.store.load();
    const rank = this.view.rank;
    if (rank === null) return null;
    const last = isRankId(state.lastShown) ? state.lastShown : null;
    if (last === rank) return null;

    const everHeld = state.everHeld.filter(isRankId);
    const awarded = last === null; // the first rank is AWARDED; every one after it is attained
    const promoted = last === null || rankIndexOf(rank) > rankIndexOf(last);
    this.deps.store.save({
      ...state,
      lastShown: rank,
      everHeld: everHeld.includes(rank) ? everHeld : [...everHeld, rank],
    });
    if (!promoted) return null; // recorded, but a demotion says nothing
    this.deps.onPromotion?.(rank, awarded);
    return { rank, awarded };
  }

  private publish(v: RankView): void {
    this.view = v;
    for (const fn of [...this.listeners]) fn(v);
  }
}

const ORDER: RankId[] = ['beggar', 'peasant', 'squire', 'knight', 'duke', 'king', 'emperor'];
const rankIndexOf = (id: RankId): number => ORDER.indexOf(id);
function isRankId(v: unknown): v is RankId {
  return typeof v === 'string' && (ORDER as string[]).includes(v);
}

export type { GuestRankRecord, RankView };
